import { useState, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import { SpinnerCircular } from 'spinners-react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { countdown } from '../state';

const NFTS = [
  { year: 2026, src: '/nft2026.mp4', to: '/nft/2026', label: '7th Birthday' },
  { year: 2025, src: '/nft2025.mp4', to: '/nft/2025', label: '6th Birthday' },
  { year: 2024, src: '/nft2024.mp4', to: '/nft/2024', label: '5th Birthday' },
  { year: 2023, src: '/nft2023.mp4', to: '/nft/2023', label: '4th Birthday' },
  { year: 2022, src: '/nft.mp4', to: '/nft/2022', label: '3rd Birthday' },
];

// This year's NFT is still being dropped, so it doesn't belong in the "past"
// gallery until the birthday deadline (UTC) has passed. Earlier years are always
// past. `bdayEnd` (UTC unix seconds) comes from cf-backend; if it hasn't loaded
// yet, fall back to the end of the birthday day for that year (00:00 UTC Jun 13).
function isPast(year, { serverNow, bdayEnd }) {
  const currentYear = moment.utc().year();
  if (year < currentYear) return true;
  if (year > currentYear) return false;
  if (!serverNow) return false; // server time not synced yet — hold off
  const deadline = bdayEnd ?? moment.utc(`${year}-06-13`).unix();
  return serverNow >= deadline;
}

const Loader = () => <SpinnerCircular size={48} thickness={180} speed={180} color="rgba(79, 172, 57, 1)" secondaryColor="rgba(0, 0, 0, 0)" />;

function NFTTile({ year, src, to, label }) {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);

  const onLoad = useCallback(() => setLoaded(true), []);
  const go = useCallback(() => navigate(to), [navigate, to]);

  return (
    <Box
      onClick={go}
      sx={{
        cursor: 'pointer',
        border: '2px #76f935 solid',
        backgroundColor: '#051b04',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(118, 249, 53, 0.25)',
        },
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1' }}>
        {!loaded && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Loader />
          </Box>
        )}
        <video
          style={{ display: loaded ? 'block' : 'none', width: '100%', height: '100%', objectFit: 'cover' }}
          onCanPlay={onLoad}
          width="100%"
          height="100%"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={src} type="video/mp4" />
        </video>
      </Box>
      <Box sx={{ py: 1, textAlign: 'center', borderTop: '2px #76f935 solid' }}>
        <Typography color="primary" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{year}</Typography>
        <Typography variant="caption" sx={{ color: '#9ee87f' }}>{label}</Typography>
      </Box>
    </Box>
  );
}

function PastNFTs() {
  const visible = NFTS.filter((nft) => isPast(nft.year, countdown));
  return (
    <Box>
      <Typography align="center" variant="h1" color="primary" sx={{ mb: 1 }}>Past Birthday NFTs</Typography>
      <Typography align="center" sx={{ mb: 4 }}>
        A commemorative NFT for every year Algorand MainNet has been live. Click one to learn more.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
        }}
      >
        {visible.map((nft) => (
          <NFTTile key={nft.year} {...nft} />
        ))}
      </Box>
    </Box>
  );
}

export default observer(PastNFTs);
